import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormFileUpload } from '@ipfabric/ui-components/FormFileUpload/FormFileUpload';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { Message } from '@ipfabric/ui-components/Message/Message';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  PRODUCT_VERSION_MAJOR,
  PRODUCT_VERSION_MINOR,
} from '@ipfabric/utils/api/version';
import {
  type ConfigurationOptionKey,
  applianceConfigurationOptions,
} from '@ipfabric/utils/applianceConfiguration/ApplianceConfiguration';
import { type FC, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import semver from 'semver/preload';
import type { ImportForm } from './importExportConfiguration.types';

interface Props {
  values: Set<string>;
  onChange: (item: Set<string>) => void;
  importFile: File | null;
  setImportFile: (file: File | null) => void;
}

export const ImportSidePanelContent: FC<Props> = (props) => {
  const [availableOptions, setAvailableOptions] = useState<string[]>([]);

  const formMethods = useFormContext<ImportForm>();
  const importEverythingFromFile = useWatch({
    control: formMethods.control,
    name: 'importEverythingFromFile',
  });
  const importFile = useWatch({
    control: formMethods.control,
    name: 'importFile',
  });

  const handleFileSelected = useStableCallback((file: File) => {
    props.setImportFile(file);
    void loadAvailableOptionsFromFile(file);
  });

  useEffect(() => {
    if (importEverythingFromFile) {
      props.onChange(new Set(availableOptions));
    } else {
      props.onChange(new Set());
    }
  }, [importEverythingFromFile, props.onChange, availableOptions]);

  useEffect(() => {
    if (importFile) {
      handleFileSelected(importFile);
    } else {
      formMethods.clearErrors('importFile');
    }
  }, [importFile, formMethods.clearErrors]);

  const loadAvailableOptionsFromFile = useStableCallback(
    async (file: File | undefined) => {
      if (file) {
        const result = await processAndValidateConfigurationFile(file);

        if (result.isValid && result.content) {
          props.onChange(new Set(result.content));
          setAvailableOptions(result.content);
        } else {
          formMethods.setError('importFile', {
            type: 'manual',
            message: result.error || 'Configuration file is invalid.',
          });
          setAvailableOptions([]);
        }
      }
    },
  );

  return (
    <form>
      <Card>
        <div className='flex flex-col gap items-start bg-default rounded-lg p'>
          <div>
            <Message
              type='warning'
              title='Warning'
              variant='plain'
              message='Imported configurations will overwrite all current settings and data - including user accounts. When the import finishes, all users are automatically logged out and will need to log in again. This action cannot be undone.'
            />
          </div>
          <FormFileUpload
            name='importFile'
            label='Select import file'
            className='w-full'
          />
          <div className='flex flex-col gap-sm'>
            <FormTextField
              control={formMethods.control}
              name='passphrase'
              type='password'
              label='Passphrase'
              description='Use the passphrase given when the configuration file was exported.'
              rules={{
                required: 'This field is required',
                minLength: {
                  value: 8,
                  message: 'Passphrase must have at least 8 characters',
                },
              }}
              startAdornment={
                <Icon glyph='fa-key' iconStyle='regular' size='sm' />
              }
            />
          </div>
        </div>
      </Card>
    </form>
  );
};

export type ConfigExportValidationResult = {
  isValid: boolean;
  error: string | undefined;
  content: string[] | undefined;
};

export const processAndValidateConfigurationFile = (
  file: File,
): Promise<ConfigExportValidationResult> => {
  return new Promise((resolve) => {
    // Check if the file has a valid extension
    const isValidExtension = file.name.toLowerCase().endsWith('.json');
    if (!isValidExtension) {
      resolve({
        isValid: false,
        error: 'The uploaded file is not a valid configuration file.',
        content: undefined,
      });
      return;
    }

    // Read the file content
    const reader = new FileReader();

    reader.onload = (event) =>
      handleFileLoad(
        event,
        resolve,
        PRODUCT_VERSION_MAJOR,
        PRODUCT_VERSION_MINOR,
      );

    reader.onerror = () => {
      resolve({
        isValid: false,
        error: 'An error occurred while reading the configuration file.',
        content: undefined,
      });
    };

    reader.readAsText(file);
  });
};

export function handleFileLoad(
  event: ProgressEvent<FileReader>,
  resolve: (value: ConfigExportValidationResult) => void,
  apiMajor: number,
  apiMinor: number,
) {
  const content = event.target?.result as string;

  const invalidConfigurationFile = () => {
    resolve({
      isValid: false,
      error: 'The uploaded configuration file is not valid.',
      content: undefined,
    });
  };

  let parsedContent: Record<string, unknown>;
  let configurationOptions: ConfigurationOptionKey[] = [];

  // try to parse the content to object
  try {
    parsedContent = JSON.parse(content);
  } catch {
    invalidConfigurationFile();
    return;
  }

  if (typeof parsedContent.version !== 'string') {
    invalidConfigurationFile();
    return;
  }
  const contentVersion = semver.parse(parsedContent.version as string);
  if (contentVersion === null) {
    invalidConfigurationFile();
    return;
  }

  // Check if version is equal or lower than the current version
  if (
    contentVersion.major > apiMajor ||
    (contentVersion.major === apiMajor && contentVersion.minor > apiMinor)
  ) {
    resolve({
      isValid: false,
      error:
        'The uploaded configuration file cannot be imported because it was created with a newer version of IP Fabric.',
      content: undefined,
    });
    return;
  }

  if (typeof parsedContent.validator !== 'string') {
    resolve({
      isValid: false,
      error:
        'The uploaded configuration file cannot be imported because it was created without a validator string.',
      content: undefined,
    });
    return;
  }

  if (typeof parsedContent.source !== 'string') {
    resolve({
      isValid: false,
      error:
        'The uploaded configuration file cannot be imported because it was created without a known source.',
      content: undefined,
    });
    return;
  }

  const allFileKeys = Object.keys(parsedContent);
  const nonConfigurationOptionKeys = [
    'version',
    'validator',
    'source',
    'databaseVersion',
  ];
  configurationOptions = allFileKeys.filter(
    (key): key is ConfigurationOptionKey =>
      !nonConfigurationOptionKeys.includes(key),
  );

  const validConfigurationKeys = configurationOptions.filter((option) =>
    applianceConfigurationOptions.includes(option),
  );
  // Check if the configuration file contains any valid keys
  if (validConfigurationKeys.length === 0) {
    resolve({
      isValid: false,
      error: 'The uploaded configuration file does not contain any valid keys.',
      content: undefined,
    });
    return;
  }

  // Check if all keys are valid
  const allConfigurationKeysAreValid =
    validConfigurationKeys.length === configurationOptions.length;
  if (!allConfigurationKeysAreValid) {
    resolve({
      isValid: true,
      error:
        'The uploaded configuration file contains invalid keys. Invalid keys will be ignored.',
      content: validConfigurationKeys,
    });
    return;
  }

  // Return validation result and content
  resolve({
    isValid: true,
    error: undefined,
    content: configurationOptions,
  });
}
